import { Quote } from '../../../types';
import { sortByColumn, SortDirection, SortColumn } from '../../../utils/sort';

export { sortByColumn as sortQuotes };
export type { SortColumn, SortDirection };

/**
 * Format a number as Brazilian currency (BRL)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Get the public PDF URL for a quote using its token
 */
export function getPublicPdfUrl(pdfToken: string): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL?.replace(/\/v1$/, '') || 'http://localhost:3001';
  return `${baseUrl}/public/quotes/${pdfToken}/pdf`;
}

/**
 * Open WhatsApp share dialog with quote PDF link
 */
export function handleShareWhatsApp(quote: Quote): void {
  if (!quote.pdfToken) return;

  const pdfUrl = getPublicPdfUrl(quote.pdfToken);
  const message = `Ola ${quote.customerName}! Segue o link do orcamento ${quote.quoteNumber}: ${pdfUrl}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  window.open(whatsappUrl, '_blank');
}

/**
 * Get the status badge variant based on quote status
 */
export function getStatusBadgeVariant(status: Quote['status']): 'pending' | 'approved' | 'rejected' {
  return status;
}

/**
 * Get the translated status label
 */
export function getStatusLabel(status: Quote['status']): string {
  const labels: Record<Quote['status'], string> = {
    pending: 'Pendente',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
  };
  return labels[status];
}

/**
 * Calculate quote totals by status
 */
export function calculateQuoteTotals(quotes: Quote[]): Record<'all' | 'pending' | 'approved' | 'rejected', number> {
  const totals = {
    all: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  };

  quotes.forEach((quote) => {
    totals.all += quote.totalAmount;
    totals[quote.status] += quote.totalAmount;
  });

  return totals;
}

/**
 * Get the payment status label in Portuguese
 */
export function getPaymentStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    link_generated: 'Link Gerado',
    pending: 'Pendente',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    refunded: 'Reembolsado',
    cancelled: 'Cancelado',
    in_process: 'Em Processamento',
    charged_back: 'Estornado',
  };
  return labels[status] || status;
}

/**
 * Get the badge variant for payment status
 */
export function getPaymentStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    link_generated: 'outline',
    pending: 'secondary',
    approved: 'default',
    rejected: 'destructive',
    refunded: 'secondary',
    cancelled: 'secondary',
    in_process: 'outline',
    charged_back: 'destructive',
  };
  return variants[status] || 'secondary';
}
